import { z } from 'zod';
import { PAYMENT_METHODS } from '../enums';
import { PHONE_E164_REGEX } from '../phone';
import { clientCreatedAt, isoDate, optText, santim, text, uuid, type SyncFields } from './common';

export const partnerSchema = z.object({
  name: text(80),
  phone: z.string().regex(PHONE_E164_REGEX).nullish(),
  joinedOn: isoDate,
  active: z.boolean().default(true),
  note: optText(500),
  clientCreatedAt,
});
export type PartnerRecord = z.output<typeof partnerSchema> & SyncFields;

export const partnerContributionSchema = z.object({
  partnerId: uuid,
  date: isoDate,
  type: z.enum(['CASH', 'ANIMAL', 'EQUIPMENT', 'LAND_USE', 'BUILDING', 'OTHER']),
  description: text(200),
  valueSantim: santim(1),
  cashReceivedSantim: santim(),
  animalId: uuid.nullish(),
  note: optText(500),
  clientCreatedAt,
}).superRefine((value, ctx) => {
  if (value.type === 'ANIMAL' && !value.animalId) ctx.addIssue({ code: 'custom', path: ['animalId'], message: 'Required' });
});
export type PartnerContributionRecord = z.output<typeof partnerContributionSchema> & SyncFields;

export const shareGrantSchema = z.object({
  partnerId: uuid,
  percentBp: z.number().int().min(1).max(10_000),
  reason: z.enum(['LABOR', 'TIME', 'MANAGEMENT', 'OTHER']),
  description: text(200),
  startDate: isoDate,
  endDate: isoDate.nullish(),
  clientCreatedAt,
}).superRefine((value, ctx) => {
  if (value.endDate && value.endDate < value.startDate) ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Before start date' });
});
export type ShareGrantRecord = z.output<typeof shareGrantSchema> & SyncFields;

export const distributionLineSchema = z.object({
  partnerId: uuid,
  sharePpm: z.number().int().min(0).max(1_000_000),
  amountSantim: santim(),
  adjusted: z.boolean().default(false),
  paidOn: isoDate.optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
  reference: z.string().trim().max(80).optional(),
}).refine((line) => !line.paidOn || !!line.method, { path: ['method'], message: 'Required when paid' });

export const distributionSchema = z.object({
  periodFrom: isoDate,
  periodTo: isoDate,
  decidedOn: isoDate,
  sharesAsOf: isoDate,
  totalSantim: santim(1),
  periodFigures: z.record(z.string(), z.unknown()),
  lines: z.array(distributionLineSchema).min(1),
  note: optText(500),
  clientCreatedAt,
});
export type DistributionRecord = z.output<typeof distributionSchema> & SyncFields;
